import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { ReceptorPromiseClient } from '@trustero/trustero-api-web/lib/agent/receptor_grpc_web_pb'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { GrcImportPromiseClient } from '@trustero/trustero-api-web/lib/importer/importer_grpc_web_pb'
import { AutoCollectRequest } from '@trustero/trustero-api-web/lib/importer/importer_pb'
import log from 'loglevel'
import {
  DocToScan,
  GetControlMappingsForReceptorRequest,
  GetControlMappingsForReceptorResponse,
  GetHasReceptorRequest,
  GetHasReceptorResponse,
  ListInProgressScansResponse,
  Enabled,
  ReceptorID,
  ReceptorRecord,
  ReceptorRecords,
  ScanMode,
  ScanNowForDirectoryScanSettingRequest,
  UpdateReceptorLabelsRequest,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useInvalidateEvidence } from 'src/Utils/swrCacheInvalidation'
import {
  useAuthorizedGrpcClient,
  useAuthorizedGrpcClientWithContentUpdate,
} from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { SwrGrpcResponse } from 'src/components/async/useSwrGrpc'
import { toast } from 'react-toastify'
import { showInfoToast } from 'src/Utils/helpers/toast'
import {
  useAccountReceptors,
  useInvalidateReceptorsCache,
} from 'src/components/async/model/receptor/useReceptors'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import { isGrpcError } from 'src/Utils/isGrpcError'
import {
  GetHasLinkerRequest,
  GetHasLinkerResponse,
  GetLinkerRequest,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { getLinkerTemplate } from 'src/lib/Linkers/Linkers.helpers'
import { useCallback, useMemo, useState } from 'react'
import { SearchableData } from 'src/Utils/search/search.types'
import { Receptor } from 'src/xgenerated/receptor'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { pickFolderScanReceptor } from './folderScanReceptor.helpers'

export const useReceptors = (
  modelId?: string,
  shouldFetch = true,
): GrpcResponse<ReceptorRecords> => {
  const request = new ReceptorID()
  modelId && request.setModelid(modelId)
  const { response } = useSwrImmutableGrpc(
    ReceptorPromiseClient.prototype.getReceptors,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export const useHasReceptor = (
  modelId: string,
  includeDeactivated = false,
  shouldFetch = true,
): GrpcResponse<GetHasReceptorResponse> => {
  const request = new GetHasReceptorRequest()
    .setModelId(modelId)
    .setIncludeDeactivated(includeDeactivated)
  const { response } = useSwrImmutableGrpc(
    ReceptorPromiseClient.prototype.getHasReceptor,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useReceptor = ({
  receptorId,
  modelId,
  shouldFetch = false,
  includeSchedule = false,
}: {
  receptorId?: string
  modelId?: string
  shouldFetch?: boolean
  includeSchedule?: boolean
}): GrpcResponse<ReceptorRecord> => {
  const request = new ReceptorID()
  const fetchReceptor = shouldFetch && (!!receptorId || !!modelId)
  receptorId && request.setOid(receptorId)
  modelId && request.setModelid(modelId)
  includeSchedule && request.setIncludeschedule(includeSchedule)
  const { response } = useSwrImmutableGrpc(
    ReceptorPromiseClient.prototype.getReceptor,
    request,
    fetchReceptor,
  )
  return NewGrpcResponse(response)
}

/**
 * The receptor row a folder scan picker or folder scan should use for a
 * document receptor model. See pickFolderScanReceptor for the precedence;
 * `preferredId` is the row the user is working on, when known.
 */
export const useFolderScanReceptor = (
  modelId: string,
  preferredId?: string,
): ReceptorRecord | undefined => {
  const { data } = useAccountReceptors()
  return useMemo(
    () =>
      pickFolderScanReceptor(data?.getReceptorsList(), modelId, preferredId),
    [data, modelId, preferredId],
  )
}

export const useScanReceptor = (): (({
  receptorId,
  receptorModelId,
  docsToScan,
}: {
  receptorId: string
  receptorModelId: string
  docsToScan?: DocToScan[]
}) => Promise<void>) => {
  const receptorClient = useAuthorizedGrpcClient(ReceptorPromiseClient)
  const mutate = useInvalidateReceptorsCache()

  return async ({
    receptorId,
    receptorModelId,
    docsToScan = [],
  }: {
    receptorId: string
    receptorModelId: string
    docsToScan?: DocToScan[]
  }) => {
    const request = new ReceptorID()
      .setOid(receptorId)
      .setModelid(receptorModelId)

    if (
      receptorModelId !== 'trr-archer' &&
      receptorModelId !== 'trr-anecdotes' &&
      receptorModelId !== 'trr-onspring' &&
      receptorModelId !== 'trr-smartsuite'
    ) {
      if (
        receptorModelId === 'trr-sharepoint' ||
        receptorModelId === 'trr-gdrive' ||
        receptorModelId === 'trr-confluence-dms' ||
        receptorModelId === 'trr-notion'
      ) {
        if (docsToScan.length > 0) {
          request.setMode(ScanMode.SCAN_MODE_SELECT_DOCS)
          request.setDocstoscanList(docsToScan)
        } else {
          request.setMode(ScanMode.SCAN_MODE_DETAILS)
        }
      }
      await receptorClient.scanNow(request)
      showInfoToast('Receptor Sync Started')
    } else {
      request.setMode(ScanMode.SCAN_MODE_METADATA)
      await receptorClient.scanNow(request)
    }
    mutate()
  }
}

export const useScanDirectorySetting = (): (({
  receptorId,
  scanSettingId,
}: {
  receptorId: string
  scanSettingId: string
}) => Promise<void>) => {
  const receptorClient = useAuthorizedGrpcClient(ReceptorPromiseClient)
  const mutate = useInvalidateReceptorsCache()

  return async ({
    receptorId,
    scanSettingId,
  }: {
    receptorId: string
    scanSettingId: string
  }) => {
    const request = new ScanNowForDirectoryScanSettingRequest()
    const recId = new ReceptorID()
      .setOid(receptorId)
      .setMode(ScanMode.SCAN_MODE_DETAILS)
    request.setId(recId)
    request.setScansettingid(scanSettingId)

    await receptorClient.scanNowForDirectoryScanSetting(request)
    showInfoToast('Folder scan started')
    mutate()
  }
}

export const useAutoCollect = (): {
  isAutoCollecting: boolean
  autoCollect: (receptorId?: string, modelid?: string) => Promise<void>
} => {
  const grcImportClient = useAuthorizedGrpcClient(GrcImportPromiseClient)
  const mutate = useInvalidateReceptorsCache()
  const [isAutoCollecting, setIsAutoCollecting] = useState(false)

  const autoCollect = useCallback(
    async (receptorId?: string, modelid?: string) => {
      if (!receptorId || !modelid || isAutoCollecting) return
      try {
        setIsAutoCollecting(true)
        const request = new AutoCollectRequest()
          .setReceptorId(receptorId)
          .setReceptorModelId(modelid)
        await grcImportClient.autoCollect(request)
        showInfoToast('Auto collection complete')
        mutate()
      } catch (err) {
        showInfoToast(
          'Sorry, there was an issue with auto collection. Please try again later.',
        )
        log.error('Failed to complete auto collect: ', err)
      } finally {
        setIsAutoCollecting(false)
      }
    },
    [grcImportClient, isAutoCollecting, mutate],
  )

  return { isAutoCollecting, autoCollect }
}

export const useInProgressReceptorScans = (
  shouldFetch = true,
  config?: SwrGrpcResponse,
): GrpcResponse<ListInProgressScansResponse> => {
  const { response } = useSwrImmutableGrpc(
    ReceptorPromiseClient.prototype.listInProgressScans,
    new Empty(),
    shouldFetch,
    config,
  )
  return NewGrpcResponse(response)
}

export const useControlMappingsForReceptor = (
  receptorId: string,
): GrpcResponse<GetControlMappingsForReceptorResponse> => {
  const request = new GetControlMappingsForReceptorRequest().setReceptorId(
    receptorId,
  )
  const { response } = useSwrImmutableGrpc(
    ReceptorPromiseClient.prototype.getControlMappingsForReceptor,
    request,
    !!receptorId,
  )
  return NewGrpcResponse(response)
}

export const useLinker = (
  modelId: LINKER_MODEL_IDS,
  shouldFetch = false,
): GrpcResponse<ReceptorRecord> => {
  const request = new GetLinkerRequest().setModelId(modelId)
  const { response } = useSwrImmutableGrpc(
    LinkerPromiseClient.prototype.getLinker,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useGetLinkers = (): ((
  modelId: LINKER_MODEL_IDS,
) => Promise<ReceptorRecord | null>) => {
  const client = useAuthorizedGrpcClient(LinkerPromiseClient)
  return useCallback(
    async (modelId: LINKER_MODEL_IDS) => {
      const linkerTemplate = getLinkerTemplate(modelId)
      try {
        const request = new GetLinkerRequest().setModelId(modelId)
        return await client.getLinker(request)
      } catch (e) {
        if (isGrpcError(e) && e.code === 5) {
          return new ReceptorRecord()
        }
        log.error(`Error getting ${linkerTemplate.name} linker`, e)
        return null
      }
    },
    [client],
  )
}

export const useHasLinker = (
  linkerId: LINKER_MODEL_IDS,
  shouldFetch = true,
): GrpcResponse<GetHasLinkerResponse> => {
  const request = new GetHasLinkerRequest()
    .setModelId(linkerId)
    .setIncludeDeactivated(false)
  const { response } = useSwrImmutableGrpc(
    LinkerPromiseClient.prototype.getHasLinker,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useSearchableReceptors = (
  receptors: Receptor[],
): SearchableData<Receptor>[] =>
  useMemo(() => {
    return prepareSearchableData(
      receptors,
      (receptor: Receptor) => receptor.id,
      (receptor: Receptor) => {
        const name = `${receptor.name} - ${receptor.tenantid}`
        return [name]
      },
    )
  }, [receptors])

export const useDeleteReceptor = (): (({
  receptorId,
}: {
  receptorId: string
}) => Promise<void>) => {
  const receptorClient = useAuthorizedGrpcClient(ReceptorPromiseClient)
  const mutate = useInvalidateReceptorsCache()

  return useCallback(
    async ({ receptorId }: { receptorId: string }) => {
      const request = new ReceptorID().setOid(receptorId)
      try {
        await receptorClient.delete(request)
        showInfoToast('Receptor deleted')
        await mutate()
      } catch (e) {
        toast.error('Failed to delete receptor')
        log.error(e)
      }
    },
    [receptorClient, mutate],
  )
}

export const useDeactivateReceptor = (): (({
  receptorId,
}: {
  receptorId: string
}) => Promise<void>) => {
  const receptorClient = useAuthorizedGrpcClientWithContentUpdate(
    ReceptorPromiseClient,
  )
  const mutate = useInvalidateReceptorsCache()

  return useCallback(
    async ({ receptorId }: { receptorId: string }) => {
      const request = new ReceptorID().setOid(receptorId)
      try {
        await receptorClient.deactivate(request)
        await mutate()
        showInfoToast('Receptor deactivated successfully')
      } catch (e) {
        if (isGrpcError(e)) {
          showInfoToast(`Error deactivating receptor: ${e.message}`)
        } else {
          showInfoToast('Error deactivating receptor')
        }
        log.error('Error deactivating receptor', e)
      }
    },
    [receptorClient, mutate],
  )
}

export const useActivateReceptor = (): (({
  receptorId,
}: {
  receptorId: string
}) => Promise<void>) => {
  const receptorClient = useAuthorizedGrpcClientWithContentUpdate(
    ReceptorPromiseClient,
  )
  const mutate = useInvalidateReceptorsCache()

  return useCallback(
    async ({ receptorId }: { receptorId: string }) => {
      const request = new Enabled()
        .setId(new ReceptorID().setOid(receptorId))
        .setIsenabled(true)
      try {
        await receptorClient.setIsEnabled(request)
        await mutate()
        showInfoToast('Receptor activated successfully')
      } catch (e) {
        if (isGrpcError(e)) {
          showInfoToast(`Error activating receptor: ${e.message}`)
        } else {
          showInfoToast('Error activating receptor')
        }
        log.error('Error activating receptor', e)
      }
    },
    [receptorClient, mutate],
  )
}

export function useGetReceptor(): (
  request: ReceptorID,
) => Promise<ReceptorRecord> {
  const client = useAuthorizedGrpcClient(ReceptorPromiseClient)
  return useCallback(
    async (request: ReceptorID) => {
      return await client.getReceptor(request)
    },
    [client],
  )
}

export const useUpdateReceptorLabels = (): (({
  receptorId,
  labelIds,
}: {
  receptorId: string
  labelIds: string[]
}) => Promise<ReceptorRecord | undefined>) => {
  const receptorClient = useAuthorizedGrpcClientWithContentUpdate(
    ReceptorPromiseClient,
  )
  const invalidateReceptors = useInvalidateReceptorsCache()
  const invalidateEvidence = useInvalidateEvidence()

  return useCallback(
    async ({
      receptorId,
      labelIds,
    }: {
      receptorId: string
      labelIds: string[]
    }): Promise<ReceptorRecord | undefined> => {
      const request = new UpdateReceptorLabelsRequest()
        .setReceptorId(receptorId)
        .setLabelIdsList(labelIds)

      const response = await receptorClient.updateReceptorLabels(request)
      await invalidateReceptors()
      await invalidateEvidence()
      return response
    },
    [receptorClient, invalidateReceptors, invalidateEvidence],
  )
}
